import React, { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function SubmitReview() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    review: ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Amazing'];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    if (formData.rating === 0) {
      newErrors.rating = 'Please select a rating';
    }
    if (!formData.review.trim()) {
      newErrors.review = 'Please write a short review';
    } else if (formData.review.trim().length < 10) {
      newErrors.review = 'Please write at least 10 characters';
    } else if (formData.review.trim().length > 500) {
      newErrors.review = 'Review must not exceed 500 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name.trim());
      if (formData.email.trim()) {
        body.append('email', formData.email.trim());
      }
      body.append(
        'rating',
        `${formData.rating} out of 5 stars (${ratingLabels[formData.rating]})`
      );
      body.append('review', formData.review.trim());
      body.append(
        'date',
        new Date().toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })
      );

      const response = await fetch(
        'https://readdy.ai/api/form/d6dsb1qtehdqnvnpj3jg',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        }
      );

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', rating: 0, review: '' });
      } else {
        setErrors({ submit: 'Something went wrong. Please try again.' });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRatingClick = (rating: number) => {
    setFormData({ ...formData, rating });
    if (errors.rating) setErrors({ ...errors, rating: '' });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const reviewJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Submit a Review – Lemonade Coffee Store',
    description: 'Share your experience at Lemonade Coffee Store. Rate our signature lemonade and Malfi Coffee drinks and help other customers discover the best beverages in Orange County, CA.',
    url: `${siteUrl}/submit-review`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Submit a Review', item: `${siteUrl}/submit-review` },
      ],
    },
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FFF8E7]">
        <SeoHead
          title="Submit a Review – Lemonade Coffee Store | Share Your Experience"
          description="Loved your Lemonade Coffee Store experience? Leave a review! Rate our Honey Lemonade Coffee, Salted Caramel Latte, and more. Your feedback helps us serve Orange County better."
          keywords="lemonade coffee review, Malfi Coffee review, coffee shop review Orange County, submit review"
          canonical={`${siteUrl}/submit-review`}
          jsonLd={reviewJsonLd}
        />
        <Navbar />
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="bg-white rounded-3xl p-12 shadow-xl">
              <div className="w-20 h-20 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-4xl text-[#2C5F2D]"></i>
              </div>
              <h1
                className="text-3xl font-bold text-[#2C5F2D] mb-4"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Thank You!
              </h1>
              <p className="text-base text-[#666] mb-8 leading-relaxed">
                Your review has been submitted! We truly appreciate your
                feedback and will add it to our website soon.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-[#FFD700] text-[#2C5F2D] px-8 py-3 rounded-full font-bold hover:bg-[#FFC700] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Submit Another Review
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      <SeoHead
        title="Submit a Review – Lemonade Coffee Store | Share Your Experience"
        description="Loved your Lemonade Coffee Store experience? Leave a review! Rate our Honey Lemonade Coffee, Salted Caramel Latte, and more. Your feedback helps us serve Orange County better."
        keywords="lemonade coffee review, Malfi Coffee review, coffee shop review Orange County, submit review"
        canonical={`${siteUrl}/submit-review`}
        jsonLd={reviewJsonLd}
      />
      <Navbar />

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-chat-smile-3-line text-3xl text-[#2C5F2D]"></i>
            </div>
            <h1
              className="text-3xl md:text-4xl font-bold text-[#2C5F2D] mb-3"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              How Was Your Visit?
            </h1>
            <p className="text-base text-[#666] leading-relaxed">
              We&apos;d love to hear from you! It only takes a minute.
            </p>
          </div>

          {/* Form */}
          <form
            id="customer-review-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 shadow-xl"
          >
            {/* Name */}
            <div className="mb-5">
              <label
                htmlFor="name"
                className="block text-sm font-bold text-[#2C5F2D] mb-2"
              >
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.name ? 'border-red-400' : 'border-[#2C5F2D]/15'
                } focus:border-[#FFD700] focus:outline-none transition-colors text-sm`}
                placeholder="e.g. Jane Smith"
              />
              {errors.name && (
                <p className="text-red-5
0 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Email (optional) */}
            <div className="mb-5">
              <label
                htmlFor="email"
                className="block text-sm font-bold text-[#2C5F2D] mb-2"
              >
                Email{' '}
                <span className="text-[#999] text-xs font-normal">
                  (optional)
                </span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 border-[#2C5F2D]/15 focus:border-[#FFD700] focus:outline-none transition-colors text-sm"
                placeholder="your@email.com"
              />
            </div>

            {/* Star Rating */}
            <div className="mb-5">
              <label className="block text-sm font-bold text-[#2C5F2D] mb-3">
                Your Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="cursor-pointer transition-transform hover:scale-125 p-1"
                  >
                    <i
                      className={`text-4xl ${
                        star <= (hoveredRating || formData.rating)
                          ? 'ri-star-fill text-[#FFD700]'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  </button>
                ))}
                {(hoveredRating > 0 || formData.rating > 0) && (
                  <span className="ml-3 text-sm font-semibold text-[#2C5F2D]">
                    {ratingLabels[hoveredRating || formData.rating]}
                  </span>
                )}
              </div>
              {errors.rating && (
                <p className="text-red-500 text-xs mt-2">{errors.rating}</p>
              )}
            </div>

            {/* Review Text */}
            <div className="mb-6">
              <label
                htmlFor="review"
                className="block text-sm font-bold text-[#2C5F2D] mb-2"
              >
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                id="review"
                name="review"
                value={formData.review}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, review: e.target.value });
                    if (errors.review) setErrors({ ...errors, review: '' });
                  }
                }}
                rows={4}
                maxLength={500}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.review ? 'border-red-400' : 'border-[#2C5F2D]/15'
                } focus:border-[#FFD700] focus:outline-none transition-colors text-sm resize-none`}
                placeholder="What did you enjoy about your visit?"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.review ? (
                  <p className="text-red-500 text-xs">{errors.review}</p>
                ) : (
                  <span className="text-xs text-[#999]">Min 10 characters</span>
                )}
                <span className="text-xs text-[#999]">
                  {formData.review.length}/500
                </span>
              </div>
            </div>

            {/* Error message */}
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                {errors.submit}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FFD700] text-[#2C5F2D] px-8 py-4 rounded-full text-lg font-bold hover:bg-[#FFC700] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill text-xl"></i>
                  Submit My Review
                </>
              )}
            </button>

            <p className="text-center text-xs text-[#999] mt-4">
              No account needed. Your review will be published after approval.
            </p>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
